/* eslint-disable @typescript-eslint/no-explicit-any */

import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import styles from './password-conditions.module.scss';

interface IPasswordConditionsProps {
  errorTypes?: Record<string, boolean>;
  passwordValue: boolean;
}

const getStatus = (conditionMet: boolean, value: boolean) => (conditionMet ? (value ? 'valid' : 'error') : 'error');

const getConditionClass = (conditionMet: boolean, value: boolean) =>
  clsx(styles.pswdCond__item, styles[`pswdCond__item--${getStatus(conditionMet, value)}`]);

const getTestId = (conditionMet: boolean, value: boolean) => `password_condition--${getStatus(conditionMet, value)}`;

const PasswordConditions: React.FC<IPasswordConditionsProps> = ({ errorTypes, passwordValue }) => {
  const { t } = useTranslation('vc-authentication');
  const { t: tA11y } = useTranslation('a11y');

  const conditions = [
    {
      key: 'minLength',
      label: t('VC_AUTHENTICATION.REGISTER_MODAL.PASSWORD_CHARACTERS_LENGTH_REQUIREMENT'),
      isMet: !errorTypes?.minLength,
    },
    {
      key: 'number',
      label: t('VC_AUTHENTICATION.REGISTER_MODAL.PASSWORD_NUMBERS_REQUIREMENT'),
      isMet: !errorTypes?.number,
    },
    {
      key: 'case',
      label: t('VC_AUTHENTICATION.REGISTER_MODAL.PASSWORD_CASE_REQUIREMENT'),
      isMet: !(errorTypes?.lowercase || errorTypes?.uppercase),
    },
  ];

  const metConditions = conditions.filter((c) => c.isMet && passwordValue);
  const allMet = metConditions.length === conditions.length;

  return (
    <>
      <div
        role="status"
        aria-atomic="true"
        aria-live="polite"
        className="visuallyhidden"
      >
        {passwordValue &&
          metConditions.length > 0 &&
          (allMet
            ? tA11y('A11Y.PASSWORD.ALL_REQUIREMENTS_MET', {
                requirements: metConditions.map((c) => c.label).join(', '),
              })
            : `${tA11y('A11Y.PASSWORD.SOME_REQUIREMENTS_MET', {
                metCondition: metConditions.length,
                totalConditions: conditions.length,
              })}: ${metConditions.map((c) => c.label).join(', ')}`)}
      </div>

      <ul className={styles.pswdCond}>
        {conditions.map(({ key, label, isMet }) => (
          <li
            key={key}
            className={getConditionClass(isMet, passwordValue)}
            data-testid={getTestId(isMet, passwordValue)}
          >
            {getStatus(isMet, passwordValue) === 'valid' ? (
              <Icon
                name="tick-fill-default"
                className={clsx(styles.pswdCond__item__icon, styles['pswdCond__item__icon--checkmark'])}
                ariaLabel={tA11y('A11Y.PASSWORD.CRITERIA_MET')}
              />
            ) : (
              <Icon
                name="clear-fill-default"
                className={clsx(styles.pswdCond__item__icon, styles['pswdCond__item__icon--cross'])}
                ariaLabel={tA11y('A11Y.PASSWORD.CRITERIA_NOT_MET')}
              />
            )}
            <span>{label}</span>
          </li>
        ))}
      </ul>
    </>
  );
};

export default PasswordConditions;
